import React, { useEffect, useState } from "react";
import { api } from "../api/client";

export type ReviewStatus = "unreviewed" | "reviewed" | "needs_correction";

export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  unreviewed: "Non relue",
  reviewed: "Relue",
  needs_correction: "À corriger",
};

/** Petite puce de couleur, utilisée aussi bien dans le sélecteur que dans
 * l'arborescence des cartes (CardTree), pour un repère visuel cohérent. */
export function ReviewStatusDot({ status }: { status: ReviewStatus | undefined }) {
  return <span className={`review-status-dot review-status-dot--${status ?? "unreviewed"}`} title={REVIEW_STATUS_LABELS[status ?? "unreviewed"]} />;
}

/** Sélecteur éditable du statut de relecture d'une carte, pour une langue
 * donnée (ou le texte original si `lang` est null/undefined). Se recharge
 * automatiquement quand la carte ou la langue affichée change. */
export function ReviewStatusSelect({
  projectId,
  cardId,
  lang,
  editable,
  onChanged,
}: {
  projectId: string;
  cardId: string;
  lang?: string | null;
  editable: boolean;
  /** Notifie le parent après une sauvegarde réussie (ex: pour rafraîchir un badge dans la liste). */
  onChanged?: (status: ReviewStatus) => void;
}) {
  const [status, setStatus] = useState<ReviewStatus>("unreviewed");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getReviewStatus(projectId, cardId, lang ?? undefined).then((r) => {
      if (!cancelled) setStatus(r.status as ReviewStatus);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, cardId, lang]);

  async function handleChange(next: ReviewStatus) {
    setStatus(next); // optimiste
    setSaving(true);
    try {
      await api.setReviewStatus(projectId, cardId, next, lang ?? undefined);
      onChanged?.(next);
    } finally {
      setSaving(false);
    }
  }

  if (!editable) {
    return (
      <span className="review-status">
        <ReviewStatusDot status={status} /> {REVIEW_STATUS_LABELS[status]}
      </span>
    );
  }

  return (
    <label className="review-status review-status--editable">
      <ReviewStatusDot status={status} />
      <select value={status} disabled={saving} onChange={(e) => handleChange(e.target.value as ReviewStatus)}>
        {(Object.keys(REVIEW_STATUS_LABELS) as ReviewStatus[]).map((s) => (
          <option key={s} value={s}>
            {REVIEW_STATUS_LABELS[s]}
          </option>
        ))}
      </select>
    </label>
  );
}
